/* eslint-disable max-lines */
"use client";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import IconButtonComponent from "../../atoms/iconButton";
import CountryListDropDown from "../countryListDropDown";
import { desktopIndiaConfig } from "../../../constants/mockData/mockData";
import Drawer from "../drawer";
import { useSelector, useDispatch } from "react-redux";
import useAnalytics from "../../../analytics/useAnalytics";
import { setIsNavigatingAction } from "../../../store/slices/commonSlice";
import { cleverTapCommonEvents, cleverTapCommonWords, reminderEvents } from "../../../analytics/clevertapConstants";
import isEmpty from "lodash.isempty";
import {
  COUNTRIES,
  COUNTRIES_URL,
  DELIVER_IN_60_COUNTRIES,
  ENGATI_ENDPOINT,
  MY_REMINDERS,
} from "../../../constants/common/common";
import { fetchSameDayCatalog } from "../../../utils/common";
import CookieUtil from "../../../utils/storage/cookieUtil";
import {
  INTERNATIONAL_PF_COOKIE,
  LAST_VISITED_INTERNATIONAL_CITY,
  LAST_VISITED_INTERNATIONAL_LOCATION,
} from "../../../constants/storage";
import { useABTestingView } from "../../../customHooks/clevertap/useABTestingView";
import CustomImage from "../../atoms/customImage";
import LottieLoader from "../../atoms/lottieLoader";
import { getLocalStorageItem, setLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import { HYDRATION_SHIELD_LINK_PROPS } from "../../reminder/reminderHydrationShieldProps";
import { MY_REMINDERS_HEADING } from "../../reminder/reminderStaticContent";

/**
 * StickyBottomMenu component for displaying a fixed bottom menu with icons and text.
 * @component
 * @param {string} param.catalogId - The catalog identifier (e.g., "india").
 * @returns {JSX.Element} JSX representation of the component.
 */
const StickyBottomMenu = ({
  catalogId = "",
  parentClass = "",
  isAllGiftActive = false,
  isMyRelationsActive = false,
  isMyAccountActive = false,
}) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const [selectedCountry, setSelectedCountry] = useState(desktopIndiaConfig?.defaultCountry);
  const [showCountryList, setShowCountryList] = useState(false);
  const [showMyRelationsPromo, setShowMyRelationsPromo] = useState(false);
  const [showGiftAssistantPopup, setShowGiftAssistantPopup] = useState(false);
  const [sameDay, setSameDay] = useState([]);
  const [activeItem, setActiveItem] = useState({
    home: !isAllGiftActive && !isMyRelationsActive && !isMyAccountActive,
    abroad: false,
    allGifts: isAllGiftActive && !isMyRelationsActive && !isMyAccountActive,
    myReminders: isMyRelationsActive,
    myAccount: isMyAccountActive,
  });
  const [loading, setLoading] = useState(false);

  const userType = useSelector((state) => state?.fus?.loginDetail?.userType);
  const countryListData = useSelector((state) => state?.locationLock?.countryListData);
  const myReminderData = useSelector((state) => state?.webFeatureControls?.data?.myReminderdata);
  const { giftAssistant } = useABTestingView();

  const options = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };
  const pageStickyMenuData = {
    page_name: cleverTapCommonWords.HOME,
    page_type: cleverTapCommonWords.HOME,
    icon_name: null,
    icon_position: null,
  };

  /** */
  const fetchSameDayData = async () => {
    const res = await fetchSameDayCatalog();
    setSameDay(res);
  };

  const reminderEventData = {
    page_name: "/",
    page_type: cleverTapCommonWords.HOME,
  };

  useEffect(() => {
    if (myReminderData && myReminderData?.isShow) {
      const myRemindersLocalStorage = getLocalStorageItem(MY_REMINDERS);
      if (myRemindersLocalStorage === "false" || isMyRelationsActive) {
        setShowMyRelationsPromo(false);
      } else {
        setShowMyRelationsPromo(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myReminderData, isMyRelationsActive]);

  useEffect(() => {
    if (catalogId !== COUNTRIES.INDIA) {
      fetchSameDayData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Array.isArray(countryListData)) {
      const country = countryListData?.filter((item) => catalogId === item?.catelogID)?.[0];

      if (!isEmpty(country)) setSelectedCountry(country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryListData]);

  useEffect(() => {
    /**
     * It will triggered when browser back button clicked.
     * @param {*} event
     */
    const handlePopState = (event) => {
      event.preventDefault();
      setShowGiftAssistantPopup(false);
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  /**
   * Toggles the visibility of the country list.
   * Sets the state to show or hide the country list.
   * @function onToggleCountryList
   */
  const onToggleCountryList = () => {
    setShowCountryList(!showCountryList);
    setActiveItem({
      home: !isAllGiftActive && !isMyAccountActive,
      abroad: false,
      allGifts: isAllGiftActive && !isMyAccountActive,
      myReminders: isMyRelationsActive,
      myAccount: isMyAccountActive,
    });
  };

  /**
   * Toggle Gift Assistant
   */
  const onToggleGiftAssistant = () => {
    if (!showGiftAssistantPopup) {
      window.history.pushState(null, null, "?promo=bottom_gifting_assistant#giftAssistant");
      trackEvent(cleverTapCommonEvents.giftFinderClicked, { from: "engati" }, ["clevertap"]);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete("promo");
      url.hash = ""; // Optional: remove hash if needed
      window.history.replaceState(null, null, url.toString());
    }
    setShowGiftAssistantPopup(!showGiftAssistantPopup);
  };

  /**
   * Handles click event for the Home menu item.
   * Toggles the active state of Home.
   * @function
   */
  const homeOnClick = () => {
    setActiveItem({
      home: true,
      myAccount: false,
      abroad: false,
      allGifts: false,
    });
  };

  /**
   * Handles click event for the Abroad menu item.
   * Toggles the active state of Abroad.
   * @function
   */
  const abroadOnClick = () => {
    setActiveItem({
      home: false,
      abroad: true,
      allGifts: false,
      myAccount: false,
    });
    setShowCountryList(true);
    trackEvent(
      cleverTapCommonEvents.bottomNavigationNavIconClicked,
      {
        ...pageStickyMenuData,
        icon_name: "Abroad",
        icon_position: 4,
      },
      ["clevertap"],
    );
  };

  /**
   * Handles the selection of a country.
   * Sets the selected country, hides the country list, and dispatches actions to update the state.
   * @function onCountryClickHandler
   * @param {Object} item - The selected country object.
   */
  const onCountryClickHandler = (item) => {
    setSelectedCountry(item);
    setLoading(true);
    if (item.countryCategoryID === "gifts") {
      window.location.href = "/?promo=bottom_abroad";
    } else if (item?.catelogID === COUNTRIES.SINGAPORE) {
      window.location.href = `${COUNTRIES_URL.SINGAPORE.url}?promo=bottom_abroad`;
    } else {
      window.location.href = `/${item.countryCategoryID}-lp?promo=bottom_abroad`;
    }
    if (catalogId !== item.catelogID) {
      CookieUtil.deleteCookie(INTERNATIONAL_PF_COOKIE, options);
      CookieUtil.deleteCookie(LAST_VISITED_INTERNATIONAL_LOCATION, options);
      CookieUtil.deleteCookie(LAST_VISITED_INTERNATIONAL_CITY, options);
    }
    setActiveItem({
      home: !isAllGiftActive && !isMyAccountActive,
      abroad: false,
      allGifts: isAllGiftActive && !isMyAccountActive,
      myReminders: isMyRelationsActive,
      myAccount: isMyAccountActive,
    });
  };

  /** */
  const onCloseMyRelationsPromo = () => {
    setShowMyRelationsPromo(false);
    setLocalStorageItem(MY_REMINDERS, "false");
    trackEvent(reminderEvents?.REMINDER_NUDGE_CLOSED, { ...reminderEventData, is_nudge_closed: true }, ["clevertap"]);
  };

  const menuItems = [
    {
      id: "home",
      iconSrc: "/icons/fnp-gift-grey.svg",
      activeIconSrc: "/icons/fnp-gift.svg",
      text: "Home",
      url: "/?promo=bottom_home",
      onClick: homeOnClick,
    },
    {
      id: "allGifts",
      iconSrc: "/icons/gift-box.svg",
      activeIconSrc: "/icons/gift-box-active.svg",
      text: "All Gifts",
      url: `/allgifts?catalogId=${catalogId}&promo=bottom_all_gifts`,
    },
    {
      id: "myReminders",
      iconSrc: "/icons/CalendarStar.svg",
      activeIconSrc: "/icons/CalendarStar-active.svg",
      text: MY_REMINDERS_HEADING,
      url: `/reminder`,
    },
    {
      id: "abroad",
      iconSrc: "/icons/global.svg",
      activeIconSrc: "/icons/global-active.svg",
      text: "Abroad",
      onClick: abroadOnClick,
    },
    // {
    //   id: "giftAssistant",
    //   iconSrc: "/icons/giftAssistant.svg",
    //   activeIconSrc: "/icons/giftAssistant.svg",
    //   text: "AI Gifting",
    //   onClick: onToggleGiftAssistant,
    // },
    {
      id: "myAccount",
      iconSrc: "/icons/user-square.svg",
      activeIconSrc: "/icons/user-square-active.svg",
      text: "Account",
      url: "/account/my-account?promo=bottom_my_account",
    },
  ];

  /** Renders the "My Relations" promo card shown above the sticky footer menu. */
  const MyRelationsPromoCard = () => {
    return (
      <>
        <div
          className="z-45 fixed left-0 top-0 h-[calc(100vh-67px)] w-full bg-black-900/50"
          onClick={onCloseMyRelationsPromo}
        />

        <div className="z-45 absolute bottom-[67px] left-8 right-8 overflow-hidden rounded-[12px] bg-white-900 p-12">
          <button
            type="button"
            aria-label="Close My Relations"
            className="absolute right-8 top-8 z-10"
            onClick={onCloseMyRelationsPromo}
          >
            <CustomImage src="/icons/drawer-close.svg" width={20} sizes="20px" height={20} alt="close icon" />
          </button>

          <div className="flex items-center justify-center gap-8">
            <div className="flex h-[60px] w-[60px] overflow-hidden rounded-[10px] bg-[#e8f5e9]">
              <CustomImage
                src={myReminderData?.iconsUrl}
                width={60}
                height={60}
                alt={myReminderData?.heading}
                sizes="60px"
                isPriority={false}
              />
            </div>

            <div className="flex flex-1 flex-col gap-8">
              <span className="text-16 font-600 leading-[16px] text-fnp-500">{myReminderData?.heading}</span>
              <span className="text-12 font-400 leading-[16px] text-fnp-500">{myReminderData?.content}</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  /**
   * Handles the click event for "same-day" items based on specific conditions.
   * Otherwise, it returns the item's landing URL as is.
   */
  const sameDayClickHandler = (item) => {
    if (item?.id === "sameDay" && !isEmpty(catalogId) && catalogId !== COUNTRIES.INDIA) {
      return `/${catalogId}${item?.url}`;
    } else if (item?.id === "home" && !isEmpty(catalogId) && catalogId !== COUNTRIES.INDIA) {
      return `/${catalogId}/gifts-lp?promo=bottom_home`;
    }
    return item?.url;
  };

  /** */
  const renderIcons = (item, index) => {
    const isShowMyRelationsPromo = showMyRelationsPromo && item.id === "myReminders";
    return (
      <li
        key={item.id}
        className={`${isShowMyRelationsPromo ? "relative z-50 rounded-b-[12px] bg-white-900" : ""} flex flex-1 flex-col items-center justify-center px-8 ${activeItem[item.id] ? "before:absolute before:top-0 before:h-4 before:w-40 before:rounded-full before:bg-fnp-600" : ""}`}
      >
        <a
          {...HYDRATION_SHIELD_LINK_PROPS}
          className="w-full py-8"
          href={sameDayClickHandler(item)}
          onClick={() => {
            if (item.id === "sameDay") {
              dispatch(setIsNavigatingAction(true));
            }
            trackEvent(
              cleverTapCommonEvents.bottomNavigationNavIconClicked,
              {
                ...pageStickyMenuData,
                icon_name: item.text,
                icon_position: index + 1,
              },
              ["clevertap"],
            );
            if (item.id === "myReminders") {
              trackEvent(
                reminderEvents.REMINDER_PAGE_VIEWED,
                {
                  ...reminderEventData,
                  sources: "bottom navigation",
                  user_type: userType,
                },
                ["clevertap"],
              );
            }

            setLocalStorageItem(MY_REMINDERS, "false");
          }}
        >
          <IconButtonComponent
            iconSrc={activeItem[item.id] ? item.activeIconSrc || item.iconSrc : item.iconSrc}
            imgHeight={35}
            imgWidth={42}
            onClick={item.onClick}
            label={item.text}
            labelColor={activeItem[item.id] ? "w-full text-fnp-300 font-normal" : "w-full text-grey-400 font-normal"}
            labelTextSize="text-10"
            classes="flex flex-col items-center justify-center gap-2 w-full"
          />
        </a>
      </li>
    );
  };

  return (
    <>
      <Drawer
        position="bottom"
        isOpen={showCountryList}
        onDrawerClose={onToggleCountryList}
        styles={`px-16 max-h-[80vh] ${showCountryList ? "pb-[40px]" : "pb-0"}`}
      >
        {loading && <LottieLoader />}
        <CountryListDropDown
          onCountryClick={onCountryClickHandler}
          selectedCountry={selectedCountry}
          isShowCountryCode={false}
          isSearchEnable={true}
          title={DELIVER_IN_60_COUNTRIES}
        />
      </Drawer>
      <Drawer
        position="bottom"
        isOpen={showGiftAssistantPopup}
        onDrawerClose={onToggleGiftAssistant}
        styles={`px-16 h-full pb-[40px]`}
        isHideCloseBtn={true}
        isDrawerCustomStyles={false}
      >
        <div
          className="-me-16 -ms-16 flex gap-12 border-b-1 border-lightGrey bg-white-900 px-16 py-12"
          onClick={onToggleGiftAssistant}
        >
          <CustomImage
            src="/icons/arrow-left-all-gifts.svg"
            alt="chat back"
            width={24}
            height={24}
            sizes="10vw"
            isPriority={false}
          />
          <span className="text-16 font-600 capitalize leading-6 text-fnp-500">Chat</span>
        </div>
        <iframe
          border="0"
          src={ENGATI_ENDPOINT}
          target="_parent"
          height="100%"
          name="Gift Assitant"
          width="100%"
          title="Gift Assistant"
          className="h-full w-full"
        />
      </Drawer>
      <div
        className={`fixed bottom-0 left-0 ${showMyRelationsPromo ? "z-50" : "z-40"} w-full justify-center bg-white-900 shadow-top md:hidden ${parentClass}`}
        id="sticky_bottom_menu"
      >
        {showMyRelationsPromo && <MyRelationsPromoCard />}
        <ul className="relative flex w-full justify-between">
          {showMyRelationsPromo && <div className="absolute left-0 top-0 z-50 h-[67px] w-full bg-black-900/50" />}
          {menuItems.map((item, index) => {
            if (
              item.id === "giftAssistant" &&
              (giftAssistant !== "true" || (giftAssistant === "true" && catalogId !== COUNTRIES.INDIA))
            ) {
              return;
            }
            return item?.url ? (
              item.id !== "sameDay" ? (
                renderIcons(item, index)
              ) : catalogId === COUNTRIES.INDIA || sameDay.includes(catalogId) || !sameDay.length ? (
                renderIcons(item, index)
              ) : null
            ) : (
              <li key={index} className="flex flex-1 flex-col items-center justify-center">
                <IconButtonComponent
                  iconSrc={activeItem[item.id] ? item.activeIconSrc || item.iconSrc : item.iconSrc}
                  imgHeight={35}
                  imgWidth={42}
                  onClick={item.onClick}
                  label={item.text}
                  labelColor={
                    activeItem[item.id] ? "w-full text-fnp-300 font-normal" : "w-full text-grey-400 font-normal"
                  }
                  labelTextSize="text-10"
                  classes="py-8 flex flex-col items-center justify-center gap-2 w-full"
                />
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

StickyBottomMenu.propTypes = {
  catalogId: PropTypes.string,
  parentClass: PropTypes.string,
  isAllGiftActive: PropTypes.bool,
  isMyRelationsActive: PropTypes.bool,
  isMyAccountActive: PropTypes.bool,
};

export default StickyBottomMenu;
